"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Search, Check, X } from "lucide-react";
import { formatCstDateTimeLocal } from "../utils/cstDate";
import { filterEmployeesByPosition, positionSupportsOweToToast } from "../utils/employeeRoleUtils";
import { calculateDynamicTax } from "../utils/dynamicTaxCalculator";
import { taxManagementService } from "../apis/taxManagementService";

export const PositionTipEditModal = ({
  isOpen,
  onClose,
  record,
  onUpdate,
  employees = [],
  positionName = "Tip",
  currentUser
}) => {
  const showOweToToast = positionSupportsOweToToast(positionName);

  const [formData, setFormData] = useState({
    date: "",
    empname: "",
    amount: "",
    owetotoast: ""
  });
  const [tax, setTax] = useState(0);
  const [taxLabel, setTaxLabel] = useState("Tax");
  const [paidOutAmount, setPaidOutAmount] = useState(0);
  const [taxSettings, setTaxSettings] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      taxManagementService.getTaxSettings().then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setTaxSettings(res.data);
        }
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (record) {
      const amountVal =
        record.amountcash || record.tip_out_cash || record.amount || "";
      const employeeName = record.employee || record.empname || "";
      const currentTaxVal = parseFloat(record?.tax || 0);
      const amtNum = parseFloat(amountVal) || 0;
      const oweVal = record.owe_to_toast || record.owetotoast || 0;

      const calc = calculateDynamicTax(amtNum, positionName, taxSettings);

      setFormData({
        date: formatCstDateTimeLocal(record.date),
        empname: employeeName,
        amount: amountVal.toString(),
        owetotoast: oweVal.toString()
      });
      // If record had tax, preserve it; otherwise use dynamic tax
      const effectiveTax = currentTaxVal > 0 ? currentTaxVal : calc.tax;
      setTax(effectiveTax);
      setTaxLabel(calc.label);
      setPaidOutAmount(Math.max(0, amtNum - effectiveTax));
      setError(null);
    }
  }, [record, taxSettings, positionName]);

  useEffect(() => {
    if (!record) return;
    const amt = parseFloat(formData.amount) || 0;
    const origAmt = parseFloat(record?.amountcash || record?.tip_out_cash || record?.amount || 0);

    // If amount changed from original, dynamically recalculate tax
    if (Math.abs(amt - origAmt) > 0.001) {
      const calc = calculateDynamicTax(amt, positionName, taxSettings);
      setTax(calc.tax);
      setTaxLabel(calc.label);
      setPaidOutAmount(Math.max(0, amt - calc.tax));
    } else {
      const currentTaxVal = parseFloat(record?.tax || 0);
      setTax(currentTaxVal);
      setPaidOutAmount(Math.max(0, amt - currentTaxVal));
    }
  }, [formData.amount, record, positionName, taxSettings]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter employees strictly for this position (supporting multi-role employees)
  const positionEmployees = useMemo(() => {
    return filterEmployeesByPosition(employees, positionName);
  }, [employees, positionName]);

  const filteredEmployees = useMemo(() => {
    if (!positionEmployees || !Array.isArray(positionEmployees)) {
      return [];
    }
    const term = searchTerm.toLowerCase();

    return positionEmployees.filter((emp) => {
      if (typeof emp === "string") {
        return emp.toLowerCase().includes(term);
      } else if (emp && typeof emp === "object" && emp.name) {
        const haystack = `${emp.name} ${emp.role || ""}`.toLowerCase();
        return haystack.includes(term);
      }
      return false;
    });
  }, [positionEmployees, searchTerm]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredEmployees.length]);

  useEffect(() => {
    if (dropdownOpen && listRef.current) {
      const container = listRef.current;
      const highlightedEl = container.querySelector(`[data-index="${highlightedIndex}"]`);
      if (highlightedEl) {
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const elTop = highlightedEl.offsetTop;
        const elBottom = elTop + highlightedEl.offsetHeight;

        if (elTop < containerTop) {
          container.scrollTop = elTop;
        } else if (elBottom > containerBottom) {
          container.scrollTop = elBottom - container.clientHeight;
        }
      }
    }
  }, [highlightedIndex, dropdownOpen]);

  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (filteredEmployees.length > 0) {
        setHighlightedIndex((prev) => (prev + 1) % filteredEmployees.length);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (filteredEmployees.length > 0) {
        setHighlightedIndex((prev) =>
          prev <= 0 ? filteredEmployees.length - 1 : prev - 1
        );
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredEmployees[highlightedIndex]) {
        handleEmployeeSelect(filteredEmployees[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      setDropdownOpen(false);
    }
  };

  const handleEmployeeSelect = (emp) => {
    const employeeName = typeof emp === "string" ? emp : emp.name;
    setFormData((prev) => ({ ...prev, empname: employeeName }));
    setDropdownOpen(false);
    setSearchTerm("");
  };

  const handleClearSelection = () => {
    setFormData((prev) => ({ ...prev, empname: "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!formData.empname || !formData.amount) {
      setError("Please fill all required fields");
      return;
    }

    const amountNumber = parseFloat(formData.amount);
    if (isNaN(amountNumber) || amountNumber <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    setSubmitting(true);
    setError(null);

    const empObj = employees.find((e) => e.name === formData.empname);
    const resolvedRole = empObj?.role || record.position || positionName;

    const isDateUnchanged =
      formData.date === formatCstDateTimeLocal(record.date);

    try {
      await onUpdate(record.id, {
        date: isDateUnchanged ? record.date : formData.date,
        empname: formData.empname,
        emprole: resolvedRole,
        position: resolvedRole,
        amountcash: amountNumber,
        amount: amountNumber,
        owetotoast: showOweToToast ? parseFloat(formData.owetotoast) || 0 : 0,
        tax: tax,
        paidout: paidOutAmount,
        added_by: currentUser?.username || "Staff"
      });
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to update record. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !record) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit {positionName} Tip Record</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Date & Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date & Time *
            </label>
            <Input
              type="datetime-local"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full"
              required
            />
          </div>

          {/* Employee Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee Name *
            </label>

            <div className="relative" ref={dropdownRef}>
              <div
                className="flex items-center justify-between border rounded-md p-2 cursor-pointer bg-white"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {formData.empname ? (
                  <div className="flex items-center">
                    <span className="mr-2 text-gray-900 font-medium">
                      {formData.empname}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearSelection();
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-400">Select employee</span>
                )}
                <svg
                  className={`w-4 h-4 ml-2 transform transition-transform ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </div>

              {dropdownOpen && (
                <div
                  ref={listRef}
                  className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                >
                  <div className="sticky top-0 bg-white p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search employees..."
                        className="pl-8"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="py-1">
                    {filteredEmployees.length > 0 ? (
                      filteredEmployees.map((emp, index) => {
                        const employeeName =
                          typeof emp === "string" ? emp : emp.name;
                        const employeeRole =
                          typeof emp === "string" ? "" : emp.role || "";
                        const employeeKey =
                          typeof emp === "string"
                            ? emp
                            : emp.id || emp.name || index;

                        const isHighlighted = index === highlightedIndex;

                        return (
                          <div
                            key={employeeKey}
                            data-index={index}
                            className={`flex items-center p-2 cursor-pointer transition-colors ${
                              isHighlighted ? "bg-blue-50" : "hover:bg-gray-100"
                            } ${
                              formData.empname === employeeName
                                ? "font-semibold text-blue-900"
                                : ""
                            }`}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            onClick={() => handleEmployeeSelect(emp)}
                          >
                            <div
                              className={`w-5 h-5 flex items-center justify-center border rounded mr-2 ${
                                formData.empname === employeeName
                                  ? "bg-blue-500 border-blue-500"
                                  : "border-gray-300"
                              }`}
                            >
                              {formData.empname === employeeName && (
                                <Check className="h-4 w-4 text-white" />
                              )}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-gray-800">{employeeName}</span>
                              {employeeRole && (
                                <span className="text-xs text-gray-500">
                                  {employeeRole}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-3 text-center text-gray-500 text-sm">
                        No {positionName.toLowerCase()} employees found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount *
            </label>
            <Input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              className="w-full"
              step="0.01"
              min="0.01"
              placeholder="Enter amount"
              required
            />
          </div>

          {/* Owe To Toast (Waitress & Bartender) */}
          {showOweToToast && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Owe To Toast
              </label>
              <Input
                type="number"
                name="owetotoast"
                value={formData.owetotoast}
                onChange={handleChange}
                placeholder="Enter amount (optional)"
                className="w-full"
                step="0.01"
                min="0"
              />
            </div>
          )}

          {/* Tax & Paid Out breakdown */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {taxLabel}
              </label>
              <div className="text-lg text-red-500 font-semibold">
                {tax > 0 ? `-$${tax.toFixed(2)}` : "-$0.00"}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paid Out
              </label>
              <div className="text-lg text-green-500 font-semibold">
                ${paidOutAmount.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Updated By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Updated By
            </label>
            <Input
              type="text"
              value={currentUser?.username || "Staff"}
              disabled
              className="w-full bg-gray-100"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-medium"
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionTipEditModal;
